import { App as CapApp } from '@capacitor/app';
import { isNative } from '../platform/capacitor';
import {
  ensurePushRegistration,
  resolveFcmTokenWithRetry,
} from '../platform/notifications';
import { syncPushTokenIfNeeded } from './deviceToken';
import { getCachedSession, saveSession } from './user';
import { setV2BearerToken } from './client';

/**
 * Merge FCM token into session and sync to backend (guest or logged-in).
 * @param {string} token
 */
export async function onFcmTokenReady(token) {
  if (!token) return;

  const current = await getCachedSession();
  const next = { ...(current || {}), pushToken: token };
  await saveSession(next);
  if (next.token) setV2BearerToken(next.token);

  const mobile = next.mobile ? String(next.mobile).replace(/\D/g, '').slice(-10) : '';
  await syncPushTokenIfNeeded({
    deviceToken: token,
    mobile: mobile.length === 10 ? mobile : undefined,
  });
}

const inFlight = new Map();

/**
 * Register for push, resolve FCM with retries, persist and sync.
 * @param {{ mobile?: string }} [options] — explicit mobile, else read from session
 */
export async function ensurePushTokenSynced(options = {}) {
  if (!isNative()) return;

  const session = await getCachedSession();
  const fromSession = session?.mobile
    ? String(session.mobile).replace(/\D/g, '').slice(-10)
    : '';
  const explicit = options.mobile
    ? String(options.mobile).replace(/\D/g, '').slice(-10)
    : '';
  const mobile = explicit.length === 10 ? explicit : fromSession;
  const flightKey = mobile.length === 10 ? mobile : 'guest';

  if (inFlight.get(flightKey)) return inFlight.get(flightKey);

  const work = (async () => {
    const granted = await ensurePushRegistration();
    if (!granted) return;

    const deviceToken = await resolveFcmTokenWithRetry();
    if (!deviceToken) return;

    await onFcmTokenReady(deviceToken);
  })().finally(() => {
    inFlight.delete(flightKey);
  });

  inFlight.set(flightKey, work);
  return work;
}

/**
 * Re-sync on every app foreground (guest = NEPTUNE, logged-in = NEPTUNE + user doc).
 * @returns {Promise<() => void>}
 */
export async function setupForegroundPushSync() {
  if (!isNative()) return () => {};

  const handle = await CapApp.addListener('appStateChange', ({ isActive }) => {
    if (!isActive) return;
    ensurePushTokenSynced();
  });

  return () => {
    handle.remove();
  };
}
